<template>
  <AppDrawer
    v-model="open"
    :title="t('login.title')"
    title-align="left"
    padded
    body-padding="18px 18px 36px"
    @back="closeLogin"
  >
    <!-- Logo 区域 -->
    <div class="logo-section">
      <span class="brand-mark">B</span>
      <h2>{{ t('login.title') }}</h2>
      <p class="subtitle">{{ t('login.subtitle') }}</p>
    </div>

    <!-- 登录方式切换 -->
    <div class="tab-switch" role="tablist" :style="{ '--i': activeIndex }">
      <span class="tab-pill" aria-hidden="true" />
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        role="tab"
        class="tab-btn"
        :aria-selected="activeTab === tab.key"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        {{ t(tab.label) }}
      </button>
    </div>

    <!-- 登录表单 -->
    <form class="login-form" @submit.prevent="onSubmit" @focusin="onFormFocusIn">
      <div :key="activeTab" class="tab-pane">
        <!-- 用户名 + 密码 -->
        <template v-if="activeTab === 'username'">
          <div class="form-group">
            <label class="field-label">{{ t('login.tabUsername') }}</label>
            <div class="input-wrapper">
              <input
                v-model="formData.username"
                type="text"
                maxlength="20"
                autocomplete="username"
                autocapitalize="off"
                autocorrect="off"
                spellcheck="false"
                enterkeyhint="next"
                :placeholder="t('login.usernamePlaceholder')"
                class="form-input"
                @input="onUsernameDomInput"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="field-label">{{ t('login.passwordLabel') }}</label>
            <div class="input-wrapper">
              <input
                v-model="formData.password"
                :type="showPassword ? 'text' : 'password'"
                maxlength="64"
                autocomplete="current-password"
                autocapitalize="off"
                autocorrect="off"
                spellcheck="false"
                enterkeyhint="go"
                :placeholder="t('login.passwordPlaceholder')"
                class="form-input"
                @input="onPasswordDomInput"
              />
              <button
                type="button"
                class="toggle-password"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                @click="showPassword = !showPassword"
              >
                <AppIcon :name="!showPassword ? 'eye-off' : 'eye'" class="ico" />
              </button>
            </div>
          </div>

          <!-- 记住我 & 忘记密码 -->
          <div class="form-actions">
            <label class="checkbox-label">
              <input v-model="formData.rememberMe" type="checkbox" class="checkbox" />
              <span>{{ t('login.rememberMe') }}</span>
            </label>
            <button type="button" class="forgot-btn" @click="onForgotPassword">
              {{ t('login.forgotPassword') }}
            </button>
          </div>
        </template>

        <!-- 邮箱 / 手机号 + 验证码 -->
        <template v-else>
          <div class="form-group">
            <label class="field-label">{{
              activeTab === 'email' ? t('login.tabEmail') : t('login.tabPhone')
            }}</label>
            <div class="input-wrapper">
              <div v-if="activeTab === 'phone'" ref="dialWrapEl" class="phone-code-wrap">
                <button type="button" class="phone-code" @click="dialOpen = !dialOpen">
                  {{ dialCode }} <span class="caret">▾</span>
                </button>
                <Transition name="dial">
                  <ul v-if="dialOpen" class="dial-menu">
                    <li
                      v-for="d in DIAL_CODES"
                      :key="d.region"
                      :class="{ on: d.code === dialCode }"
                      @click="pickDial(d.code)"
                    >
                      <span>{{ d.region }}</span>
                      <span class="dial-num">{{ d.code }}</span>
                    </li>
                  </ul>
                </Transition>
              </div>
              <input
                v-model="contactAccount"
                :type="activeTab === 'email' ? 'email' : 'tel'"
                :inputmode="activeTab === 'email' ? 'email' : 'tel'"
                :autocomplete="activeTab === 'email' ? 'email' : 'tel-national'"
                autocapitalize="off"
                spellcheck="false"
                enterkeyhint="next"
                :maxlength="activeTab === 'email' ? 128 : 16"
                :placeholder="
                  activeTab === 'email' ? 'you@example.com' : t('login.phonePlaceholder')
                "
                class="form-input"
                @input="onAccountDomInput"
                @beforeinput="onPhoneBeforeInput"
                @paste="onPhonePaste"
              />
            </div>
          </div>

          <div class="form-group">
            <label class="field-label">{{ t('login.verifyCode') }}</label>
            <div class="input-wrapper">
              <input
                v-model="contactCode"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                enterkeyhint="go"
                maxlength="6"
                :placeholder="
                  activeTab === 'email'
                    ? t('login.emailCodePlaceholder')
                    : t('login.smsCodePlaceholder')
                "
                class="form-input"
                @input="onCodeDomInput"
                @beforeinput="onCodeBeforeInput"
                @paste="onCodePaste"
              />
              <button type="button" class="code-btn" :disabled="codeCounting" @click="onSendCode">
                {{ codeCounting ? `${codeRemaining}s` : t('login.getCode') }}
              </button>
            </div>
          </div>
        </template>
      </div>

      <!-- 统一错误提示：仅显示在登录按钮上一行 -->
      <Transition name="err">
        <span v-if="formError" class="field-error" role="alert">{{ formError }}</span>
      </Transition>

      <!-- 登录/注册按钮 -->
      <button type="submit" class="submit-btn" :disabled="isLoading">
        <span v-if="isLoading" class="btn-spinner" aria-hidden="true" />
        {{ isLoading ? t('login.loading') : t('login.submit') }}
      </button>
    </form>

    <!-- 快捷登录 -->
    <div class="quick-login">
      <div class="divider">
        <span>{{ t('login.quickLogin') }}</span>
      </div>
      <div class="social-icons">
        <div class="google-slot">
          <button type="button" class="social-btn" @click="onGoogleClick">📧</button>
          <div class="gsi-overlay" :class="{ off: !formData.agreed }" aria-hidden="true">
            <div ref="gsiButtonEl" class="gsi-scale" />
          </div>
        </div>
        <button v-if="telegramEnabled" type="button" class="social-btn" @click="onTelegramClick">
          ✈️
        </button>
        <button
          type="button"
          class="social-btn"
          :disabled="isLoading"
          aria-label="MetaMask"
          @click="onMetamaskClick"
        >
          🦊
        </button>
      </div>
    </div>

    <!-- 用户协议 -->
    <div class="agreement">
      <label class="checkbox-label">
        <input v-model="formData.agreed" type="checkbox" class="checkbox" />
        <span class="agreement-text">
          {{ t('login.agreementPrefix') }}
          <a href="#" class="link" @click.prevent="onOpenTerms">{{ t('login.userAgreement') }}</a>
          {{ t('login.and') }}
          <a href="#" class="link" @click.prevent="onOpenPrivacy">{{ t('login.privacyPolicy') }}</a>
          {{ t('login.agreementSuffix') }}
        </span>
      </label>
    </div>
  </AppDrawer>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import AppIcon from '~/components/AppIcon.vue'
import type { OAuthProvider } from '~/types/api/login'

const { t } = useI18n()
const { open, closeLogin } = useAuthNav()
const { openForgotPassword } = useForgotPasswordNav()
const { openAgreement } = useAgreementNav()
const { openPrivacy } = usePrivacyNav()
const { login, oauthLogin, sendLoginCode, metamaskLogin } = useAuth()
const { success: notifySuccess, info: notifyInfo } = useNotify()

type LoginTab = 'username' | 'email' | 'phone'

const tabs = [
  { key: 'username' as LoginTab, label: 'login.tabUsername' },
  { key: 'email' as LoginTab, label: 'login.tabEmail' },
  { key: 'phone' as LoginTab, label: 'login.tabPhone' },
]

const activeTab = ref<LoginTab>('username')
const activeIndex = computed(() => tabs.findIndex(tab => tab.key === activeTab.value))
const showPassword = ref(false)
const isLoading = ref(false)

const formData = reactive({
  username: '',
  password: '',
  email: '',
  emailCode: '',
  phone: '',
  phoneCode: '',
  rememberMe: true,
  agreed: true,
})

/**
 * 登录表单全输入框禁汉字：直接清洗响应式值，v-model 回写 DOM。
 * watch 数据层而非拦截输入事件，可覆盖 IME 组合提交 / 粘贴 / 浏览器自动填充等所有路径。
 */
watch(formData, () => {
  const username = formData.username.replace(/[^A-Za-z0-9_]/g, '')
  if (username !== formData.username) formData.username = username

  const password = formData.password.replace(/[^\x21-\x7E]/g, '')
  if (password !== formData.password) formData.password = password

  const email = sanitizeEmail(formData.email)
  if (email !== formData.email) formData.email = email

  const emailCode = formData.emailCode.replace(/\D/g, '')
  if (emailCode !== formData.emailCode) formData.emailCode = emailCode

  const phone = formData.phone.replace(/\D/g, '')
  if (phone !== formData.phone) formData.phone = phone

  const phoneCode = formData.phoneCode.replace(/\D/g, '')
  if (phoneCode !== formData.phoneCode) formData.phoneCode = phoneCode
})

/**
 * 输入事件即时清 DOM：IME 组合中的汉字等非法字符在落框瞬间被抹掉（组合即中断）。
 * v-model 会读到已清洗的值；watch(formData) 做数据层兜底。
 */
const cleanInputEl = (event: Event, re: RegExp) => {
  const el = event.target as HTMLInputElement
  const cleaned = el.value.replace(re, '')
  if (cleaned !== el.value) el.value = cleaned
}
const onUsernameDomInput = (e: Event) => cleanInputEl(e, /[^A-Za-z0-9_]/g)
const onPasswordDomInput = (e: Event) => cleanInputEl(e, /[^\x21-\x7E]/g)
const onAccountDomInput = (e: Event) => {
  const el = e.target as HTMLInputElement
  const cleaned =
    activeTab.value === 'phone' ? el.value.replace(/\D/g, '') : sanitizeEmail(el.value)
  if (cleaned !== el.value) el.value = cleaned
}
const onCodeDomInput = (e: Event) => cleanInputEl(e, /\D/g)

const emailCountdown = useSmsCountdown(() => `login-email-code:${formData.email}`)
const phoneCountdown = useSmsCountdown(() => `login-phone-code:${formData.phone}`)

const activeCountdown = computed(() =>
  activeTab.value === 'phone' ? phoneCountdown : emailCountdown,
)
const codeCounting = computed(() => activeCountdown.value.isCounting.value)
const codeRemaining = computed(() => activeCountdown.value.remaining.value)

const contactAccount = computed({
  get: () => (activeTab.value === 'email' ? formData.email : formData.phone),
  set: (val: string) => {
    if (activeTab.value === 'email') formData.email = val
    else formData.phone = val
  },
})

const contactCode = computed({
  get: () => (activeTab.value === 'email' ? formData.emailCode : formData.phoneCode),
  set: (val: string) => {
    if (activeTab.value === 'email') formData.emailCode = val
    else formData.phoneCode = val
  },
})

/** 验证码仅允许数字：beforeinput 阶段拦截非数字字符（键盘/输入法），非输入类操作（删除等）放行 */
const onCodeBeforeInput = (event: InputEvent) => {
  if (event.data && /\D/.test(event.data)) event.preventDefault()
}

/** 粘贴含非数字字符时整块拒绝，纯数字则由 maxlength 截断到 6 位 */
const onCodePaste = (event: ClipboardEvent) => {
  const text = event.clipboardData?.getData('text') ?? ''
  if (/\D/.test(text)) event.preventDefault()
}

/** 手机号仅允许数字：区号由下拉选择提供，禁止手输 +（避免与所选区号冲突） */
const onPhoneBeforeInput = (event: InputEvent) => {
  if (activeTab.value !== 'phone' || !event.data) return
  if (/\D/.test(event.data)) event.preventDefault()
}

const onPhonePaste = (event: ClipboardEvent) => {
  if (activeTab.value !== 'phone') return
  const text = event.clipboardData?.getData('text') ?? ''
  if (/\D/.test(text)) event.preventDefault()
}

const DIAL_CODES = [
  { region: 'CN', code: '+86' },
  { region: 'HK', code: '+852' },
  { region: 'MO', code: '+853' },
  { region: 'TW', code: '+886' },
  { region: 'SG', code: '+65' },
  { region: 'MY', code: '+60' },
  { region: 'TH', code: '+66' },
  { region: 'VN', code: '+84' },
  { region: 'ID', code: '+62' },
  { region: 'PH', code: '+63' },
  { region: 'IN', code: '+91' },
  { region: 'JP', code: '+81' },
  { region: 'KR', code: '+82' },
  { region: 'AE', code: '+971' },
  { region: 'RU', code: '+7' },
  { region: 'GB', code: '+44' },
  { region: 'US/CA', code: '+1' },
  { region: 'AU', code: '+61' },
  { region: 'BR', code: '+55' },
]

const dialCode = ref('+86')
const dialOpen = ref(false)
const dialWrapEl = ref<HTMLElement | null>(null)
onClickOutside(dialWrapEl, () => {
  dialOpen.value = false
})
const pickDial = (code: string) => {
  dialCode.value = code
  dialOpen.value = false
}

/** 后端要求 E.164：已带 + 原样使用，否则补所选区号（去掉手机号前导 0） */
const toE164 = (raw: string): string => {
  const v = raw.replace(/\s/g, '')
  return v.startsWith('+') ? v : `${dialCode.value}${v.replace(/^0+/, '')}`
}

const onSendCode = () => {
  const type = activeTab.value === 'phone' ? 'phone' : 'email'
  const account = type === 'phone' ? toE164(formData.phone) : formData.email.trim()
  // 先校验格式再发码，避免无效请求被后端 400 拒绝
  if (type === 'phone' && !isE164(account)) {
    formError.value = t('login.errorPhoneInvalid')
    return
  }
  if (type === 'email' && !isEmail(account)) {
    formError.value = t('login.errorEmailInvalid')
    return
  }
  activeCountdown.value.send(() => sendLoginCode(type, account))
}

const formError = ref('')

watch(activeTab, () => {
  formError.value = ''
})

const resetForm = () => {
  formData.username = ''
  formData.password = ''
  formData.email = ''
  formData.emailCode = ''
  formData.phone = ''
  formData.phoneCode = ''
  showPassword.value = false
  formError.value = ''
}

const validateForm = (): boolean => {
  formError.value = ''

  if (activeTab.value === 'username') {
    if (!formData.username) {
      formError.value = t('login.errorUsernameRequired')
    } else if (!isValidUsername(formData.username)) {
      formError.value = t('login.errorUsernameInvalid')
    } else if (!formData.password) {
      formError.value = t('login.errorPasswordRequired')
    } else if (formData.password.length < 8 || formData.password.length > 64) {
      formError.value = t('login.errorPasswordLength')
    } else if (!hasLetterAndDigit(formData.password)) {
      formError.value = t('login.errorPasswordLetterDigit')
    }
  } else if (activeTab.value === 'email') {
    if (!isEmail(formData.email) || formData.email.length > 128) {
      formError.value = t('login.errorEmailInvalid')
    } else if (!/^\d{6}$/.test(formData.emailCode)) {
      formError.value = t('login.errorEmailCodeRequired')
    }
  } else {
    if (!isE164(toE164(formData.phone))) {
      formError.value = t('login.errorPhoneInvalid')
    } else if (!/^\d{6}$/.test(formData.phoneCode)) {
      formError.value = t('login.errorPhoneCodeRequired')
    }
  }

  if (!formError.value && !formData.agreed) {
    formError.value = t('login.errorAgreementRequired')
  }

  return !formError.value
}

/** 登录成功统一收尾：toast 由全局层独立展示，这里只留短暂停顿让用户看到「成功」再收起抽屉 */
const finishLogin = async () => {
  notifySuccess(t('login.loginSuccess'))
  await new Promise(resolve => setTimeout(resolve, 600))
  resetForm()
  closeLogin()
}

/** 软键盘弹出后把当前输入框滚到可视区中部，避免被键盘遮住 */
const onFormFocusIn = (event: FocusEvent) => {
  const el = event.target as HTMLInputElement | null
  if (!el || el.tagName !== 'INPUT' || el.type === 'checkbox') return
  setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300)
}

const onSubmit = async () => {
  if (!validateForm()) return

  isLoading.value = true
  try {
    await login({
      type: activeTab.value,
      account:
        activeTab.value === 'username'
          ? formData.username
          : activeTab.value === 'email'
            ? formData.email
            : toE164(formData.phone),
      password: activeTab.value === 'username' ? formData.password : undefined,
      code:
        activeTab.value === 'email'
          ? formData.emailCode
          : activeTab.value === 'phone'
            ? formData.phoneCode
            : undefined,
    })
    await finishLogin()
  } catch (error) {
    formError.value = toErrorText(error)
    console.error('Login failed:', error)
  } finally {
    isLoading.value = false
  }
}

const onForgotPassword = () => {
  openForgotPassword(formData.username)
}

// —— 🦊 MetaMask 钱包签名登录（SIWE）：连接钱包 → 服务端消息 → personal_sign → 换 JWT ——

/** 断网 / 请求未到达服务器（fetch 无响应），避免把底层 [POST] "url" Failed to fetch 原文展示给用户 */
const isNetworkError = (error: unknown): boolean => {
  if (import.meta.client && !navigator.onLine) return true
  if (!(error instanceof Error)) return false
  const e = error as Error & { response?: unknown }
  return (
    (e.name === 'FetchError' && !e.response) || /failed to fetch|network ?error/i.test(e.message)
  )
}

const toErrorText = (error: unknown): string => {
  if (isNetworkError(error)) return t('login.errorNetwork')
  return error instanceof Error && error.message ? error.message : t('login.errorLoginFailed')
}

const onMetamaskClick = async () => {
  if (isLoading.value) return
  if (!formData.agreed) {
    formError.value = t('login.errorAgreementRequired')
    return
  }
  isLoading.value = true
  formError.value = ''
  try {
    await metamaskLogin()
    await finishLogin()
  } catch (error) {
    // 未安装 / 用户拒绝 / 连接失败 / 验签失败：统一展示在错误行
    formError.value = toErrorText(error)
    console.error('[login] metamask login failed:', error)
  } finally {
    isLoading.value = false
  }
}

// —— 📧 Google 登录（GIS One Tap）：ID Token 换 JWT 后与账密登录同路径 ——

const GSI_SRC = 'https://accounts.google.com/gsi/client'
const TG_LOGIN_SRC = 'https://telegram.org/js/telegram-login.js'

// 渠道开关与 SDK 公开标识均来自 GET /account/oauth/config；抽屉打开时才请求
const { googleClientId, googleEnabled, telegramBotId, telegramEnabled } = useOAuthConfig(open)

/** 第三方脚本按 src 全局只加载一次；失败清除缓存允许重试 */
const scriptLoads = new Map<string, Promise<void>>()
const loadScript = (src: string) => {
  let task = scriptLoads.get(src)
  if (!task) {
    task = new Promise<void>((resolve, reject) => {
      const el = document.createElement('script')
      el.src = src
      el.async = true
      el.onload = () => resolve()
      el.onerror = () => reject(new Error(`script load failed: ${src}`))
      document.head.appendChild(el)
    }).catch(error => {
      scriptLoads.delete(src)
      throw error
    })
    scriptLoads.set(src, task)
  }
  return task
}

const onOAuthCredential = async (provider: OAuthProvider, credential: string) => {
  if (isLoading.value) return
  isLoading.value = true
  formError.value = ''
  try {
    await oauthLogin(provider, credential)
    await finishLogin()
  } catch (error) {
    formError.value = toErrorText(error)
    console.error(`[login] ${provider} login failed:`, error)
  } finally {
    isLoading.value = false
  }
}

// —— 📧 Google 登录（GIS 官方按钮弹窗，不再使用有冷却期的 One Tap） ——

/** Google Identity Services 最小类型 */
interface GsiIdApi {
  initialize: (config: {
    client_id: string
    callback: (response: { credential?: string }) => void
    use_fedcm_for_prompt?: boolean
  }) => void
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void
}

const gsiButtonEl = ref<HTMLElement | null>(null)

/** GIS icon 按钮固定 40x40（跨域 iframe，脚本点不到），需要用户真实点击，故透明叠在 📧 上并缩放到同尺寸 */
const GSI_ICON_SIZE = 40

/** initialize 只允许调一次：重复调用 GIS 会告警且仅末次生效 */
let gsiInitialized = false

/** 预载脚本、initialize 并把官方按钮渲染到隐藏容器（抽屉关闭再打开会重建容器，需重渲染） */
const prepareGsi = async (): Promise<boolean> => {
  const clientId = googleClientId.value
  if (!clientId) return false
  try {
    await loadScript(GSI_SRC)
  } catch (error) {
    console.error('[login] load GSI failed:', error)
    return false
  }
  await nextTick()
  const gsi = (window as unknown as { google?: { accounts?: { id?: GsiIdApi } } }).google?.accounts
    ?.id
  if (!gsi) return false
  if (!gsiInitialized) {
    gsiInitialized = true
    gsi.initialize({
      client_id: clientId,
      callback: response => {
        if (!formData.agreed) {
          formError.value = t('login.errorAgreementRequired')
          return
        }
        if (response.credential) void onOAuthCredential('google', response.credential)
      },
      use_fedcm_for_prompt: true,
    })
  }
  const el = gsiButtonEl.value
  if (!el) return false
  if (!el.hasChildNodes()) {
    gsi.renderButton(el, { type: 'icon', size: 'large', shape: 'square' })
  }
  const host = el.parentElement
  if (host) {
    el.style.transform = `scale(${host.offsetWidth / GSI_ICON_SIZE}, ${host.offsetHeight / GSI_ICON_SIZE})`
  }
  return true
}

// 抽屉容器（ClientOnly + Teleport）晚于 open 挂载，挂载完成后再渲染一次
watch(gsiButtonEl, el => {
  if (el && googleEnabled.value) void prepareGsi()
})

const onGoogleClick = async () => {
  if (!googleClientId.value) {
    notifyInfo(t('login.socialUnavailable'))
    return
  }
  if (!formData.agreed) {
    formError.value = t('login.errorAgreementRequired')
    return
  }
  // 正常情况下点击被透明的官方按钮拦截；走到这里说明官方按钮尚未就绪，补渲染后让用户再点一次
  if (!(await prepareGsi())) formError.value = t('login.errorLoginFailed')
}

// 配置到手且抽屉打开即预载已启用渠道的脚本并预渲染 Google 按钮，首次点击无需等待（弹窗须在点击同步链内调用，否则会被浏览器拦截）
watch(
  [open, googleEnabled, googleClientId, telegramEnabled],
  ([isOpen, gOn, , tOn]) => {
    if (!isOpen) return
    if (gOn) void prepareGsi()
    if (tOn) void loadScript(TG_LOGIN_SRC).catch(() => {})
  },
  { immediate: true },
)

// —— ✈️ Telegram 登录（Log In With Telegram，OIDC）：弹窗回调拿到 id_token（JWT），后端用 JWKS 验签 ——
// 旧版 telegram-widget.js 的 oauth.telegram.org/auth?bot_id= 已弃用（返回 deprecated），需用 telegram-login.js

/** telegram-login.js 回调：成功带 id_token，失败带 error（用户关闭弹窗为 'popup_closed'） */
type TelegramLoginResult = { id_token: string; user?: Record<string, unknown> } | { error: string }

interface TelegramLoginApi {
  auth: (
    options: { client_id: string; scope?: string; lang?: string; nonce?: string },
    callback: (result: TelegramLoginResult) => void,
  ) => void
}

const getTelegramLogin = () =>
  (window as unknown as { Telegram?: { Login?: TelegramLoginApi } }).Telegram?.Login

const onTelegramClick = async () => {
  const botId = telegramBotId.value
  if (!botId) {
    notifyInfo(t('login.socialUnavailable'))
    return
  }
  if (!formData.agreed) {
    formError.value = t('login.errorAgreementRequired')
    return
  }
  // 脚本已预载时同步直达 auth，保持在点击手势内避免弹窗被拦截
  if (!getTelegramLogin()) {
    try {
      await loadScript(TG_LOGIN_SRC)
    } catch (error) {
      console.error('[login] load Telegram login failed:', error)
      formError.value = t('login.errorLoginFailed')
      return
    }
  }
  const tg = getTelegramLogin()
  if (!tg) {
    formError.value = t('login.errorLoginFailed')
    return
  }
  tg.auth({ client_id: botId }, result => {
    if ('error' in result) {
      // 用户关闭弹窗静默；其余错误（域名未登记等）提示并留日志
      if (result.error === 'popup_closed') {
        console.warn('[login] telegram popup closed without id_token')
      } else {
        console.error('[login] telegram auth error:', result.error)
        formError.value = t('login.errorLoginFailed')
      }
      return
    }
    void onOAuthCredential('telegram', result.id_token)
  })
}

const onOpenTerms = () => {
  openAgreement()
}

const onOpenPrivacy = () => {
  openPrivacy()
}
</script>

<style scoped lang="scss">
.logo-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin: 8px 0 20px;

  .brand-mark {
    width: 56px;
    height: 56px;
    border-radius: var(--r-lg);
    display: grid;
    place-items: center;
    color: #fff;
    font-weight: 900;
    font-size: 30px;
    background: var(--brand-mark);
    box-shadow: var(--brand-glow);
  }

  h2 {
    font-size: 20px;
    font-weight: 900;
  }

  .subtitle {
    font-size: 12px;
    color: var(--muted);
  }
}

.tab-switch {
  position: relative;
  display: flex;
  background: var(--surface2);
  border-radius: var(--r-md);
  padding: 4px;
  gap: 4px;

  /* 滑块：3 等分，宽 = (总宽 - 左右 padding 8 - 2 个 gap 8) / 3；位移步长 = 自身宽 + gap */
  .tab-pill {
    position: absolute;
    top: 4px;
    bottom: 4px;
    left: 4px;
    width: calc((100% - 16px) / 3);
    border-radius: 9px;
    background: var(--surface);
    box-shadow: var(--shadow-sm);
    transform: translateX(calc(var(--i, 0) * (100% + 4px)));
    transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  .tab-btn {
    position: relative;
    z-index: 1;
    flex: 1;
    padding: 11px 9px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 700;
    color: var(--muted);
    transition: color 0.2s ease-out;

    &.active {
      color: var(--text);
    }

    &:active {
      opacity: 0.7;
    }
  }
}

.tab-pane {
  animation: login-pane-in 0.22s ease-out;
}

@keyframes login-pane-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
}

@keyframes login-spin {
  to {
    transform: rotate(360deg);
  }
}

.form-group {
  margin-top: 14px;
}

.field-label {
  font-size: 12px;
  color: var(--muted);
  font-weight: 600;
  display: block;
  margin-bottom: 7px;
}

.field-error {
  display: block;
  margin-top: 6px;
  font-size: 11px;
  color: var(--danger, #e53935);
  font-weight: 600;
}

.err-enter-active,
.err-leave-active {
  transition:
    opacity 0.2s ease-out,
    transform 0.2s ease-out;
}

.err-enter-from,
.err-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.dial-enter-active {
  transition:
    opacity 0.16s ease-out,
    transform 0.16s ease-out;
}

.dial-leave-active {
  transition:
    opacity 0.12s ease-in,
    transform 0.12s ease-in;
}

.dial-enter-from,
.dial-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--surface2);
  border: 1px solid var(--hairline);
  border-radius: var(--r-md);
  padding: 12px 14px;
  transition:
    border-color 0.18s ease-out,
    box-shadow 0.18s ease-out;

  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.18);
  }

  .form-input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    color: var(--text);
    font-size: 15px;
    font-weight: 700;
  }

  .toggle-password {
    width: 36px;
    height: 36px;
    margin: -5px -7px -5px 0;
    display: grid;
    place-items: center;
    padding: 0;
    color: var(--muted);

    &:active {
      opacity: 0.6;
    }

    .ico {
      font-size: 14px;
    }
  }

  .phone-code-wrap {
    position: relative;
  }

  .dial-menu {
    position: absolute;
    top: calc(100% + 14px);
    left: -14px;
    z-index: 5;
    min-width: 140px;
    max-height: 240px;
    overflow-y: auto;
    margin: 0;
    padding: 4px;
    list-style: none;
    background: var(--surface);
    border: 1px solid var(--hairline);
    border-radius: var(--r-md);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);

    li {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      padding: 9px 10px;
      border-radius: var(--r-sm);
      font-size: 14px;
      font-weight: 700;
      color: var(--text);
      cursor: pointer;

      &.on {
        color: var(--primary);
      }

      &:active {
        background: var(--surface2);
      }
    }

    .dial-num {
      color: var(--muted);
    }
  }

  .phone-code {
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 0 8px 0 0;
    border-right: 1px solid var(--hairline);
    color: var(--text);
    font-size: 15px;
    font-weight: 700;
    white-space: nowrap;

    .caret {
      font-size: 10px;
      color: var(--muted);
    }
  }

  .code-btn {
    padding: 8px 0 8px 8px;
    margin: -8px 0;
    color: var(--primary);
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;

    &:disabled {
      color: var(--muted);
      cursor: not-allowed;
    }

    &:active:not(:disabled) {
      opacity: 0.6;
    }
  }
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: var(--muted);

  .checkbox {
    width: 15px;
    height: 15px;
    margin: 0;
    accent-color: var(--primary);
  }
}

.forgot-btn {
  padding: 8px 0 8px 8px;
  margin: -8px 0;
  color: var(--primary);
  font-weight: 700;
  font-size: 12px;

  &:active {
    opacity: 0.6;
  }
}

.btn-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 8px;
  vertical-align: -2px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  animation: login-spin 0.7s linear infinite;
}

.submit-btn {
  margin-top: 16px;
  width: 100%;
  transition:
    transform 0.15s ease-out,
    opacity 0.2s ease-out;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--primary);
  box-shadow: var(--btn-glow);
  color: #fff;
  font-size: 14px;
  font-weight: 800;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
    transition-duration: 0.06s;
  }
}

.divider {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--muted);
  font-size: 11px;
  margin: 20px 0 14px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--hairline);
  }
}

.social-icons {
  display: flex;
  gap: 10px;
}

.google-slot {
  position: relative;
  flex: 1;
  display: flex;

  .social-btn {
    flex: 1;
  }
}

.gsi-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  overflow: hidden;
  opacity: 0.01;

  &.off {
    pointer-events: none;
  }
}

.gsi-scale {
  width: 40px;
  height: 40px;
  transform-origin: 0 0;
}

.social-btn {
  flex: 1;
  height: 48px;
  border-radius: var(--r-md);
  background: var(--surface);
  border: 1px solid var(--hairline);
  display: grid;
  place-items: center;
  font-size: 22px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  transition: transform 0.15s ease-out;

  &:active:not(:disabled) {
    transform: scale(0.97);
    transition-duration: 0.06s;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tab-pill,
  .tab-btn,
  .tab-pane,
  .input-wrapper,
  .submit-btn,
  .social-btn,
  .btn-spinner,
  .err-enter-active,
  .err-leave-active,
  .dial-enter-active,
  .dial-leave-active {
    animation: none !important;
    transition: none !important;
  }
}

.agreement {
  margin-top: 14px;

  .checkbox-label {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12px;
    color: var(--muted);
    line-height: 1.5;
  }

  .link {
    color: var(--text);
  }
}
</style>
